// Cross-tenant maintenance analytics / sessions / reporting API.
// Migrated from the Redash internal dashboards. All read-only
// (super-admin + maintenance-viewer). Base = /api/maintenance.
import axiosInstance from "../axiosInstance";
import type { PaginatedResponse } from "./index";

const BASE = "/api/maintenance";

// ── Analytics (Overview dashboard) ───────────────────────────────────────
export interface KeyMetrics {
  active_users_45d: number;
  sessions_this_month: number;
  energy_this_month_kwh: number | null;
  revenue_this_month: number | null;
  avg_duration_mins: number | null;
  active_stations: number;
  inactive_stations: number;
  total_stations: number;
}

export interface DailyTrendPoint {
  day: string;
  sessions: number;
  unique_users: number;
  energy_kwh: number;
  revenue: number;
  successful: number;
  zero_energy: number;
  incomplete: number;
  avg_kwh_per_session: number | null;
}

export interface PeakHourCell { day_num: number; hour: number; sessions: number; }
export interface StatusBreakdown { status: string; sessions: number; }
export interface UserSegment { user_segment: string; user_count: number; }
export interface UserGrowthPoint { day: string; new_users: number; cumulative_users: number; }
export interface CohortRow {
  cohort: string; cohort_size: number;
  month_0: number; month_1: number; month_2: number; month_3: number; month_4: number; month_5: number;
}
export interface OcppVersionUsage { ocpp_version: string; sessions: number; }
export interface VehicleUsage { vehicle: string; sessions: number; energy_kwh: number; }
export interface DeviceOnboardingPoint { day: string; new_devices: number; cumulative_devices: number; }
export interface TopStation { station: string; station_guid: string | null; sessions: number; energy_kwh: number; }
export interface OrgRollup {
  org_id: number; organization: string; active_users: number; stations: number;
  sessions: number; active_users_window: number; energy_kwh: number; revenue: number;
}
export interface PowerUser {
  user_id: number; display_name: string | null; phone: string | null; organization: string | null;
  total_sessions: number; total_kwh: number; total_spent: number | null; last_session: string | null;
}
export interface ActiveUser {
  user_id: number; user_guid: string | null; display_name: string | null; phone: string | null;
  email: string | null; organization: string | null; last_session: string | null; sessions: number;
}
export interface StationMapPoint {
  station: string; station_guid: string | null; lat: number | null; lon: number | null;
  text_address: string | null; organization: string | null; sessions_30d: number; kwh_30d: number; status: string;
}
export interface UserInfo {
  user_id: number; user_name: string | null; phone: string | null; email: string | null;
  user_guid: string | null; status: string | null; organization: string | null; wallet_balance: number | null;
}
export interface UserStats { total_sessions: number; total_kwh: number | null; total_spent: number | null; last_session: string | null; }
export interface UserSessionRow {
  session_id: number; session_started_on: string | null; station: string | null; device: string | null;
  connector_name: string | null; duration_mins: number | null; consumed_kwh: number | null;
  charge_rate_kw: number | null; total_cost: number | null; status: string | null;
}
export interface UserVehicle { vehicle_name: string | null; vehicle_model: string | null; }
export interface UserDetail { user: UserInfo; stats: UserStats; sessions: UserSessionRow[]; vehicles: UserVehicle[]; }

// ── Sessions / live ops ──────────────────────────────────────────────────
export interface ActiveSession {
  session_id: number; session_started_on: string | null; mins_active: number | null;
  user_id: number | null; user_name: string | null; phone: string | null; organization: string | null;
  station_name: string | null; device: string | null; device_name: string | null;
  connector_name: string | null; meter_start: number | null; meter_stop: number | null;
  consumed_kwh: number | null; charge_rate_kw: number | null; status: string | null;
}
export interface ConnectorStatus {
  connector_id: number; device: string | null; unique_name: string | null; connector_name: string | null;
  capacity_kwh: number | null; station: string | null; organization: string | null;
  current_status: string | null; effective_status: string | null; socket_name: string | null;
  last_active: string | null; online: boolean; error_code: string | null; error_info: string | null;
  sessions_30d: number; last_session: string | null;
}
export interface RecentSession {
  session_id: number; session_started_on: string | null; session_stopped_on: string | null;
  duration_mins: number | null; user_name: string | null; phone: string | null; organization: string | null;
  station: string | null; device: string | null; connector_name: string | null;
  consumed_kwh: number | null; total_cost: number | null; charge_rate_kw: number | null;
  status: string | null; stop_reason: string | null;
}
export interface MeterValuePoint {
  event_time: string; measurand: string | null; phase: string | null; unit: string | null; value: number | null;
}
export interface SessionDetail {
  session_id: number; session_guid: string | null; status: string | null; finished: boolean | null;
  session_started_on: string | null; session_stopped_on: string | null; duration_mins: number | null;
  user_id: number | null; user_name: string | null; phone: string | null; email: string | null; organization: string | null;
  station: string | null; text_address: string | null;
  device: string | null; device_name: string | null; ocpp_version: string | null;
  connector_name: string | null; connector_description: string | null; capacity_kwh: number | null; socket_name: string | null;
  vehicle_name: string | null; vehicle_model: string | null;
  meter_start: number | null; meter_stop: number | null; consumed_kwh: number | null;
  rate_kwh: number | null; total_cost: number | null; charge_rate_kw: number | null; stop_reason: string | null;
}

// ── Transactions / wallets (reporting) ───────────────────────────────────
export type GroupBy = "date" | "station" | "user" | "org";
export interface TxnAggregateRow {
  session_date: string | null; station_id: number | null; station_name: string | null;
  user_id: number | null; user_name: string | null; org_id: number | null; organization: string | null;
  total_sessions: number; consumed_kwh: number; total_amount: number;
}
export interface TxnDetailRow {
  transaction_id: number; transaction_amount: number;
  wallet_balance_before: number | null; wallet_balance_after: number | null;
  transaction_state: string | null; transaction_date: string; transaction_type: string | null;
  session_id: number | null; consumed_kwh: number | null; rate_kwh: number | null;
  vehicle_name: string | null; device_used: string | null;
  session_started_on: string | null; session_stopped_on: string | null;
  user_name: string | null; organization: string | null;
}
export interface UserWalletBalance {
  wallet_id: number; user_id: number; user_name: string | null; phone: string | null;
  organization: string | null; current_balance: number;
}
export interface CategorySummaryRow {
  category: string; count: number; total: number; abs_total: number;
}
export interface WalletMovement {
  transaction_id: number; transaction_amount: number;
  wallet_balance_before: number | null; wallet_balance_after: number | null;
  transaction_state: string | null; transaction_date: string; transaction_type: string | null;
  user_name: string | null; phone: string | null; organization: string | null;
}

type P = Record<string, any>;

export const maintAnalyticsApi = {
  // analytics
  keyMetrics: () => axiosInstance.get<KeyMetrics>(`${BASE}/analytics/key-metrics`),
  dailyTrend: (params?: P) => axiosInstance.get<DailyTrendPoint[]>(`${BASE}/analytics/trends/daily`, { params }),
  peakHours: (params?: P) => axiosInstance.get<PeakHourCell[]>(`${BASE}/analytics/peak-hours`, { params }),
  sessionStatus: (params?: P) => axiosInstance.get<StatusBreakdown[]>(`${BASE}/analytics/session-status`, { params }),
  userSegments: () => axiosInstance.get<UserSegment[]>(`${BASE}/analytics/user-segments`),
  userGrowth: (params?: P) => axiosInstance.get<UserGrowthPoint[]>(`${BASE}/analytics/user-growth`, { params }),
  cohortRetention: () => axiosInstance.get<CohortRow[]>(`${BASE}/analytics/cohort-retention`),
  ocppVersions: (params?: P) => axiosInstance.get<OcppVersionUsage[]>(`${BASE}/analytics/ocpp-versions`, { params }),
  vehicleUsage: (params?: P) => axiosInstance.get<VehicleUsage[]>(`${BASE}/analytics/vehicle-usage`, { params }),
  deviceOnboarding: () => axiosInstance.get<DeviceOnboardingPoint[]>(`${BASE}/analytics/device-onboarding`),
  topStations: (params?: P) => axiosInstance.get<TopStation[]>(`${BASE}/analytics/top-stations`, { params }),
  orgs: (params?: P) => axiosInstance.get<OrgRollup[]>(`${BASE}/analytics/orgs`, { params }),
  powerUsers: (params?: P) => axiosInstance.get<PowerUser[]>(`${BASE}/analytics/power-users`, { params }),
  activeUsers: (params?: P) => axiosInstance.get<ActiveUser[]>(`${BASE}/analytics/active-users`, { params }),
  userDetail: (userId: number) => axiosInstance.get<UserDetail>(`${BASE}/analytics/users/${userId}`),
  stationMap: (params?: P) => axiosInstance.get<StationMapPoint[]>(`${BASE}/analytics/station-map`, { params }),

  // sessions / live ops
  activeSessions: (params?: P) => axiosInstance.get<ActiveSession[]>(`${BASE}/sessions/active`, { params }),
  connectorStatus: (params?: P) => axiosInstance.get<ConnectorStatus[]>(`${BASE}/sessions/connector-status`, { params }),
  recentSessions: (params?: P) => axiosInstance.get<RecentSession[]>(`${BASE}/sessions/recent`, { params }),
  sessionDetail: (sessionId: number) => axiosInstance.get<SessionDetail>(`${BASE}/sessions/${sessionId}`),
  meterValues: (sessionId: number) => axiosInstance.get<MeterValuePoint[]>(`${BASE}/sessions/${sessionId}/meter-values`),

  // transactions / wallets
  txnAggregate: (params?: P) => axiosInstance.get<TxnAggregateRow[]>(`${BASE}/reporting/transactions`, { params }),
  txnDetail: (params?: P) => axiosInstance.get<PaginatedResponse<TxnDetailRow>>(`${BASE}/reporting/transactions/detail`, { params }),
  txnExportUrl: `${BASE}/reporting/transactions/export`,
  txnExport: (params?: P) => axiosInstance.get(`${BASE}/reporting/transactions/export`, { params, responseType: "blob" }),
  userBalances: (params?: P) => axiosInstance.get<UserWalletBalance[]>(`${BASE}/reporting/wallets/user-balances`, { params }),
  rechargesRefunds: (params?: P) => axiosInstance.get<WalletMovement[]>(`${BASE}/reporting/wallets/recharges-refunds`, { params }),
  categorySummary: (params?: P) => axiosInstance.get<CategorySummaryRow[]>(`${BASE}/reporting/wallets/category-summary`, { params }),
};
